import React from "react";
import { Link } from "react-router-dom";
import { Compass, Target, CalendarDays, GitBranch, MapPin, Sparkles, ArrowRight, BookOpen, Trophy, Building2, GraduationCap, ListTodo } from "lucide-react";

/**
 * Programme — static, graphically-appealing overview of iTeams.
 * Content sourced from the iTeams Innovation Programme 2026 brief.
 * The Sessions page (/workbook) houses the per-session detail, so this page
 * is intentionally non-interactive — it sets the framing.
 */

const SECTIONS = [
  {
    key: "what",
    label: "What",
    icon: Compass,
    accent: "#25AAAA",
    body: `iTeams is an immersive, practitioner-led innovation programme that brings together participants from different disciplines, experiences and perspectives to address real-world challenges presented by external organisations. Working as part of a team, you will investigate your challenge, understand the wider system surrounding it and explore the needs of the people and organisations involved. You will generate ideas, test assumptions and develop a solution that is desirable, feasible and commercially viable.\n\nThis is not simply a classroom exercise. It is an opportunity to work with the uncertainty and complexity of a genuine challenge and turn your collective thinking into a clear, persuasive proposal.`,
  },
  {
    key: "why",
    label: "Why",
    icon: Target,
    accent: "#F4B847",
    body: `The purpose of iTeams is to bridge the gap between academic learning and innovation in practice. Real-world problems rarely arrive neatly defined or with one obvious answer. They require curiosity, collaboration, creativity and the confidence to explore before reaching conclusions.\n\nThroughout the programme, you will use commercial and creative problem-solving techniques alongside soft systems approaches. You will learn to examine a challenge from different perspectives, recognise relationships within the wider system and identify where meaningful change may be possible. The experience will also help you develop the human capabilities that make innovation possible: listening, communicating, managing disagreement, working across disciplines and building trust within a team.`,
  },
  {
    key: "when",
    label: "When",
    icon: CalendarDays,
    accent: "#A23232",
    body: `Eight sessions across seven weeks, from 28 October to 9 December 2026. Sessions run 6:00–9:00pm on Wednesdays (with an additional Thursday for Session 2 on 29 October).\n\nEach session builds on the one before. Team formation kicks things off, sponsor businesses present in Session 2, and the programme culminates on Wednesday 9 December with a live pitching session in which every team presents its evidence-based solution to the partner organisations and judging panel.`,
  },
  {
    key: "how",
    label: "How",
    icon: GitBranch,
    accent: "#0F1F2E",
    body: `You will work in a multidisciplinary team assigned to a real sponsor challenge. Together you will question the initial brief, investigate the people, organisations and systems connected to it, and develop a coherent proposal.\n\nThe Weave will facilitate each session, creating a supportive but challenging environment in which teams can experiment, reflect and develop. You will also be supported by experienced mentors who will guide, challenge and — occasionally — cajole you along the way. Each mentor brings a wealth of professional experience and a different perspective on your challenge. Make full use of them: ask questions, test your thinking, and listen carefully to their feedback.\n\nThe strongest teams remain curious, coachable and open to experience.`,
  },
  {
    key: "where",
    label: "Where",
    icon: MapPin,
    accent: "#25AAAA",
    body: `iTeams is delivered at the Innovation Centre, Knowledge Gateway, University of Essex, Colchester — a purpose-built home for creative and commercial thinking. This becomes your team's headquarters for the seven weeks: a place to collaborate, sketch, argue, refine and rehearse.\n\nBring your energy, your openness and your commitment. The more you put in, the more valuable the experience becomes — for you, your team and the sponsor business you'll help.`,
  },
];

const QUICK_LINKS = [
  { to: "/workbook", label: "Sessions", caption: "All 8 sessions — brief, resources, outputs", icon: BookOpen },
  { to: "/my-sponsor", label: "My Sponsor", caption: "Your assigned business + your problem statement", icon: Building2 },
  { to: "/mentor-booking", label: "Book a Mentor", caption: "Grab a slot from the 8-hour online pool", icon: GraduationCap },
  { to: "/tasks", label: "Team Tasks", caption: "Split the work up. Track what's done.", icon: ListTodo },
];

export default function Programme() {
  return (
    <div className="space-y-12" data-testid="programme-page">
      {/* Hero */}
      <section className="relative border-2 border-[#0F1F2E] bg-[#0F1F2E] text-white overflow-hidden">
        {/* Decorative concentric rings — Weave motif in iTeams palette */}
        <div className="absolute -right-32 -bottom-32 w-[420px] h-[420px] rounded-full border border-[#F4B847]/20 pointer-events-none" />
        <div className="absolute -right-20 -bottom-20 w-[300px] h-[300px] rounded-full border border-[#25AAAA]/30 pointer-events-none" />
        <div className="absolute -right-10 -bottom-10 w-[180px] h-[180px] rounded-full border border-[#A23232]/40 pointer-events-none" />

        <div className="relative p-8 md:p-12">
          <div className="text-[10px] font-semibold tracking-[0.24em] uppercase text-[#F4B847]" data-testid="programme-eyebrow">
            iTeams Innovation Programme · Autumn 2026
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-3 leading-[0.95]">
            Build the team.<br />
            <span className="text-[#F4B847]">Explore the challenge.</span><br />
            <span className="text-[#25AAAA]">Create the solution.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-sm md:text-base leading-relaxed text-neutral-200">
            An eight-session innovation programme built around real-world organisational challenges.
            Work in a multidisciplinary team, explore a complex problem and develop a commercially
            credible, creative response. Along the way, strengthen your problem-solving,
            communication, teamwork and presentation skills.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/workbook"
              className="inline-flex items-center gap-2 bg-[#F4B847] text-[#0F1F2E] px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#dba134] transition-colors"
              data-testid="programme-cta-workbook"
            >
              Open the Sessions <ArrowRight size={14} />
            </Link>
            <Link
              to="/my-sponsor"
              className="inline-flex items-center gap-2 border border-white/40 px-5 py-3 text-xs font-bold uppercase tracking-wider hover:bg-white hover:text-[#0F1F2E] transition-colors"
              data-testid="programme-cta-sponsor"
            >
              See your Sponsor
            </Link>
          </div>
        </div>
      </section>

      {/* Five W's & H — magazine spread */}
      <section className="space-y-0" data-testid="programme-sections">
        {SECTIONS.map((s, i) => {
          const Icon = s.icon;
          const isOdd = i % 2 === 1;
          return (
            <article
              key={s.key}
              className={`grid grid-cols-1 md:grid-cols-12 gap-0 border-b border-[#E5E7EB] ${
                isOdd ? "md:[direction:rtl]" : ""
              }`}
              data-testid={`programme-section-${s.key}`}
            >
              {/* Label column */}
              <div
                className="md:col-span-4 p-8 md:p-10 flex flex-col justify-between md:[direction:ltr]"
                style={{ background: `${s.accent}10` }}
              >
                <div>
                  <div
                    className="inline-flex items-center justify-center w-12 h-12 rounded-none mb-6"
                    style={{ background: s.accent }}
                  >
                    <Icon size={22} className="text-white" />
                  </div>
                  <div className="text-[10px] font-semibold tracking-[0.16em] uppercase" style={{ color: s.accent }}>
                    {String(i + 1).padStart(2, "0")} · Section
                  </div>
                  <h2 className="font-display text-5xl md:text-7xl font-black tracking-tighter mt-2 text-[#0F1F2E]">
                    {s.label}?
                  </h2>
                </div>
                <div className="hidden md:block mt-8 text-[10px] uppercase tracking-[0.2em] text-[#525252]">
                  iTeams Innovation · Autumn 2026
                </div>
              </div>

              {/* Body column */}
              <div className="md:col-span-8 p-8 md:p-12 bg-white md:[direction:ltr] flex items-center">
                <p className="text-[15px] md:text-base leading-relaxed text-[#0F1F2E] whitespace-pre-line max-w-3xl">
                  {s.body}
                </p>
              </div>
            </article>
          );
        })}
      </section>

      {/* Final thoughts callout */}
      <section
        className="border-2 border-[#0F1F2E] bg-[#F4B847] p-8 md:p-12 relative overflow-hidden"
        data-testid="programme-final-thoughts"
      >
        <div className="absolute left-[-40px] top-[-40px] opacity-[0.08] pointer-events-none"
             style={{
               width: 280, height: 280,
               backgroundImage: "repeating-linear-gradient(45deg, #0F1F2E 0 1px, transparent 1px 16px)",
             }} />
        <div className="relative max-w-3xl">
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#0F1F2E]">A final word</div>
          <h2 className="font-display text-3xl md:text-4xl font-black tracking-tighter mt-2 text-[#0F1F2E] leading-[1.02]">
            Stay curious. Stay coachable. Make the most of every session.
          </h2>
          <p className="mt-5 text-sm md:text-base leading-relaxed text-[#0F1F2E]">
            Confidence is valuable, but do not let it become arrogance or assume that you have nothing to
            learn. The strongest teams remain curious, coachable and open to experience — and they lift
            each other up along the way.
          </p>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-[#0F1F2E]">
            iTeams is a rare chance to experiment, learn and lead in a supportive environment, surrounded
            by peers, mentors and real partner organisations. What you take from it is up to you.
          </p>
        </div>
      </section>

      {/* Quick links */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#E5E7EB]" data-testid="programme-quick-links">
        {QUICK_LINKS.map((q) => {
          const Icon = q.icon;
          return (
            <Link
              key={q.to}
              to={q.to}
              className="group p-6 border-r last:border-r-0 border-[#E5E7EB] bg-white hover:bg-[#0F1F2E] hover:text-white transition-colors"
              data-testid={`programme-quick-link-${q.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <Icon size={20} className="text-[#25AAAA] group-hover:text-[#F4B847]" />
              <div className="font-display text-xl font-black tracking-tighter mt-3">{q.label}</div>
              <div className="text-xs text-[#525252] group-hover:text-neutral-300 mt-1">{q.caption}</div>
              <div className="mt-4 text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1">
                Open <ArrowRight size={11} />
              </div>
            </Link>
          );
        })}
      </section>
    </div>
  );
}
